import { ref, computed, ComputedRef, reactive } from 'vue'
import { useStore } from 'vuex'
import {
  getPartCollections,
  getPartsForCollection,
} from '@/controllers/library/productPage'
import type {
  PartCollectionParamsDto
} from '@/interfaces/library/partCollection'

const collections = ref<any[]>([])
const expandedIds = ref<Set<number>>(new Set())
const isExpandingAll = ref(false)
const shouldKeepAllExpanded = ref(false)
const selectedCollectionId = ref<number | null>(null)
const activeProductPageId = ref<number | null>(null)

const isLoadingCollections = ref(false)
const isLoadingMoreCollections = ref(false)
const hasMoreCollections = ref(true)

const params = reactive<PartCollectionParamsDto>({
  offset: 0,
  limit: 20
})

const collectionIdToPageIdMap: ComputedRef<Map<number, number>> = computed(() => {
  return new Map(
    collections.value.map((collection: any) => [
      collection.id,
      collection.pageId,
    ])
  )
})

const hasAnExpandedCollection = computed(() => {
  return expandedIds.value.size > 0
})

function resetState(params?: { offset?: number }) {
  collections.value = []
  expandedIds.value = new Set()
  isExpandingAll.value = false
  selectedCollectionId.value = null
  activeProductPageId.value = null
  isLoadingCollections.value = false
  isLoadingMoreCollections.value = false
  hasMoreCollections.value = true
  shouldKeepAllExpanded.value = false

  if (params) {
    params.offset = 0
  }
}

export function usePartCollectionsData(
  productPageId: number,
  normalizePart: (part: any) => any
) {
  const store = useStore()
  const hideRetailPrice = computed(() => {
    return store.state.user.pricing.hideRetailPrice
  })
  const hideWholesalePrice = computed(() => {
    return store.state.user.pricing.hideWholeSalePrice
  })
  const hideDiscountPrice = computed(() => {
    return store.state.user.pricing.hideDiscountPrice
  })

  function toggleShowRetailPrice(collectionId: number) {
    const collection = collections.value
      .find((collection: any) => collection.id === collectionId)
    if (collection) {
      collection.showRetail = !collection.showRetail
    }
  }

  function toggleShowWholesalePrice(collectionId: number) {
    const collection = collections.value
      .find((collection: any) => collection.id === collectionId)
    if (collection) {
      collection.showWholesale = !collection.showWholesale
    }
  }

  function toggleShowDiscountPrice(collectionId: number) {
    const collection = collections.value
      .find((collection: any) => collection.id === collectionId)
    if (collection) {
      collection.showDiscount = !collection.showDiscount
    }
  }

  function findCollection(collectionId: number) {
    return collections.value.find((collection: any) => collection.id === collectionId)
  }

  function normalizeCollection(raw: any) {
  return {
    ...raw,
    id: Number(raw.id),
    pageId: raw.pageId == null ? null : Number(raw.pageId),
    name: raw.name ?? '',
    description: raw.description ?? '',
    partCount: raw.partCount ?? raw.parts?.length ?? 0,
    parts: Array.isArray(raw.parts) ? raw.parts : [],
    partsLoaded: !!raw.partsLoaded && Array.isArray(raw.parts),
    partsLoading: !!raw.partsLoading,
    showRetail: !hideRetailPrice.value,
    showWholesale: !hideWholesalePrice.value,
    showDiscount: !hideDiscountPrice.value
  }
}

  async function loadCollections() {
    if (
      activeProductPageId.value !== null &&
      activeProductPageId.value !== productPageId
    ) {
      resetState(params)
    }

    if (isLoadingCollections.value) return

    isLoadingCollections.value = true

    try {
      params.offset = 0

      const requestParams = {
        ...params,
        offset: 0
      }

      const { data } = await getPartCollections(productPageId, requestParams)
      const normalized = (data ?? []).map(normalizeCollection)

      collections.value = normalized
      activeProductPageId.value = productPageId
      hasMoreCollections.value = normalized.length === params.limit
    } finally {
      isLoadingCollections.value = false
    }
  }

async function loadMoreCollections() {
  if (isLoadingCollections.value) return
  if (isLoadingMoreCollections.value) return
  if (!hasMoreCollections.value) return
  if (activeProductPageId.value !== productPageId) return

  isLoadingMoreCollections.value = true

  try {
    const nextOffset = collections.value.length

    const requestParams = {
      ...params,
      offset: nextOffset
    }

    const { data } = await getPartCollections(productPageId, requestParams)
    const normalized = (data ?? []).map(normalizeCollection)

    if (normalized.length === 0) {
      hasMoreCollections.value = false
      return
    }

    const seen = new Set(collections.value.map((collection: any) => collection.id))
    const deduped = normalized.filter((collection: any) => !seen.has(collection.id))

    collections.value = [...collections.value, ...deduped]
    params.offset = nextOffset
    hasMoreCollections.value = normalized.length === params.limit

    if (shouldKeepAllExpanded.value && deduped.length > 0) {
      isExpandingAll.value = true
      try {
        await Promise.all(
          deduped.map((collection: any) => loadPartsForCollection(collection.id))
        )
        expandedIds.value = new Set(collections.value.map(collection => collection.id))
      } finally {
        isExpandingAll.value = false
      }
    }
  } finally {
    isLoadingMoreCollections.value = false
  }
}

  async function loadPartsForCollection(collectionId: number) {
    const collection = findCollection(collectionId)

    if (!collection || collection.partsLoaded || collection.partsLoading) {
      return
    }

    collection.partsLoading = true

    try {
      const { data } = await getPartsForCollection(productPageId, collectionId)

      collection.parts = (data ?? []).map(normalizePart)
      collection.partsLoaded = true
      collection.partCount = collection.partCount ?? collection.parts.length
    } finally {
      collection.partsLoading = false
      collection.showRetail = !hideRetailPrice.value
      collection.showWholesale = !hideWholesalePrice.value
      collection.showDiscount = !hideDiscountPrice.value
    }
  }

  async function toggleCollection(collectionId: number, isSearchMode: boolean) {
    if (isSearchMode) return

    selectedCollectionId.value = null
    const nextExpanded = new Set(expandedIds.value)

    if (nextExpanded.has(collectionId)) {
      nextExpanded.delete(collectionId)
      expandedIds.value = nextExpanded
      return
    }

    await loadPartsForCollection(collectionId)
    nextExpanded.add(collectionId)
    expandedIds.value = nextExpanded
  }

async function expandAll(isSearchMode: boolean) {
  if (isSearchMode) return

  shouldKeepAllExpanded.value = true
  isExpandingAll.value = true

  const targetCollections = collections.value.filter(
    collection => !collection.partsLoaded
  )

  try {
    await Promise.all(
      targetCollections.map(collection =>
        loadPartsForCollection(collection.id)
      )
    )

    expandedIds.value = new Set(collections.value.map(collection => collection.id))
  } finally {
    isExpandingAll.value = false
  }
}

function collapseAll(isSearchMode: boolean) {
  if (isSearchMode) return
  shouldKeepAllExpanded.value = false
  expandedIds.value = new Set()
}

  function selectCollection(collectionId: number | null) {
    selectedCollectionId.value = collectionId
  }

  function resetIfOwner() {
    if (activeProductPageId.value === productPageId) {
      resetState(params)
    }
  }

  return {
    collections,
    expandedIds,
    isExpandingAll,
    selectedCollectionId,
    collectionIdToPageIdMap,
    isLoadingCollections,
    isLoadingMoreCollections,
    hasMoreCollections,
    hasAnExpandedCollection,
    loadCollections,
    loadMoreCollections,
    loadPartsForCollection,
    toggleCollection,
    expandAll,
    collapseAll,
    selectCollection,
    resetIfOwner,
    toggleShowRetailPrice,
    toggleShowWholesalePrice,
    toggleShowDiscountPrice
  }
}
